import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import StripePayment from '../../components/StripePayment';
import { toast } from 'react-toastify';
import useRequest from 'hooks/useRequest';
import { useBasket } from 'context/BasketContext';
import { useOrder } from 'context/OrderContext';
import { useSocket } from 'context/SocketClusterContext';
import SubmittingOrderOverlay from '../../components/SubmittingOrderOverlay';

const PaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { sendRequest } = useRequest();
  const { basketItems } = useBasket();
  const { startMatchingProcess, prescriptionId } = useOrder();
  const { onRefetchData } = useSocket();
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [showPaymentOverlay, setShowPaymentOverlay] = useState(false);
  const [paymentConfirmationTime, setPaymentConfirmationTime] = useState(null);

  const { deliveryType, pharmacy, tip } = location.state || {};

  // Create order when component mounts
  useEffect(() => {
    const createOrder = async () => {
      if (
        deliveryType == null ||
        !pharmacy
      ) {
        toast.error('Missing order information');
        navigate('/basket');
        return;
      }

      setIsCreatingOrder(true);

      const hasHighQuantity = basketItems.some(item => item.quantity > 3);
      const selectedForWhom = localStorage.getItem('selectedForWhom');

      const orderData = {
        type: deliveryType,
        products: basketItems,
        pharmacyId: pharmacy.pharmacy_id,
        tip: tip,
        ...(hasHighQuantity && {
          forMyself: selectedForWhom === 'myself' ? 1 : 0,
          forOthers: selectedForWhom === 'others' ? 1 : 0
        }),
        ...(prescriptionId && {
          prescriptionId: prescriptionId
        })
      };

      try {
        const response = await sendRequest({
          method: 'POST',
          url: `/module/order/createOrder`,
          data: orderData
        });

        if (response.status === 'error') {
          toast.error(response.message || 'Failed to create order');
          navigate('/basket');
        } else {
          setOrderDetails(response.data);
          setOrderId(response.data.id);
          setIsCreatingOrder(false);
        }
      } catch (error) {
        toast.error('Failed to create order. Please try again.');
        navigate('/basket');
      }
    };

    createOrder();
  }, [deliveryType, pharmacy, basketItems, prescriptionId, sendRequest, navigate]);

  // Wait for payment confirmation via socket (fast path) or polling (fallback).
  // Socket: NotificationSender pushes ORDER_PAID instantly when webhook fires.
  // Polling: Every 3s we check if the order's paymentId was set by the webhook.
  // Whichever arrives first wins.
  useEffect(() => {
    if (!showPaymentOverlay || !orderId) return;

    let timeoutId;
    let minDelayTimeoutId;
    let pollIntervalId;
    let hasNavigated = false;

    const handlePaymentConfirmed = () => {
      if (hasNavigated) return;
      hasNavigated = true;

      if (timeoutId) clearTimeout(timeoutId);
      if (pollIntervalId) clearInterval(pollIntervalId);

      setPaymentConfirmationTime(Date.now());

      const overlayShowTime = Date.now() - (window.paymentOverlayStartTime || Date.now());
      const minimumShowTime = 4000;
      const remainingTime = Math.max(0, minimumShowTime - overlayShowTime);

      minDelayTimeoutId = setTimeout(() => {
        toast.success('Payment completed successfully!');
        setShowPaymentOverlay(false);

        if (orderDetails) {
          startMatchingProcess(orderDetails);

          let nextStep = `/matchmaking/customer/0`;
          let instructions = 'PICKUP_ORDER_CREATED_INSTRUCTIONS';

          if (deliveryType === 1) {
            instructions = 'COLLECT_ORDER_CREATED_INSTRUCTIONS';
          } else if (deliveryType === 2) {
            instructions = 'MAIL_ORDER_CREATED_INSTRUCTIONS';
          }

          navigate('/instruction', { state: { instructions, nextStep } });
        }
      }, remainingTime);
    };

    // Final timeout: 30s (increased since polling gives us a reliable fallback)
    timeoutId = setTimeout(() => {
      if (hasNavigated) return;
      hasNavigated = true;
      if (pollIntervalId) clearInterval(pollIntervalId);
      toast.error('Payment confirmation timeout. Please check your order status.');
      setShowPaymentOverlay(false);
      navigate('/basket');
    }, 30000);

    // Fast path: socket notification
    const cleanup = onRefetchData((data, socket) => {
      if (data && data.notificationType === 'ORDER_PAID') {
        console.log('Payment confirmed via socket for order:', orderId);

        if (data.notificationId && socket) {
          socket.emit('notificationAck', { notificationId: data.notificationId });
        }

        handlePaymentConfirmed();
      }
    });

    // Fallback: poll order status every 3s to check if webhook set paymentId
    const orderUUID = orderDetails?.integrationId;
    if (orderUUID) {
      pollIntervalId = setInterval(async () => {
        if (hasNavigated) return;
        try {
          const response = await sendRequest({
            method: 'POST',
            url: `/module/order/getOrder`,
            data: { orderUUID }
          });

          if (response.status === 'success' && response.data?.deliveryStatus >= 1) {
            console.log('Payment confirmed via polling for order:', orderId);
            handlePaymentConfirmed();
          }
        } catch (err) {
          // Poll failed, will retry on next interval
        }
      }, 3000);
    }

    return () => {
      if (cleanup) cleanup();
      if (timeoutId) clearTimeout(timeoutId);
      if (minDelayTimeoutId) clearTimeout(minDelayTimeoutId);
      if (pollIntervalId) clearInterval(pollIntervalId);
    };
  }, [showPaymentOverlay, orderId, orderDetails, deliveryType, navigate, startMatchingProcess, onRefetchData, sendRequest]);

  const handlePaymentSuccess = (paymentIntent) => {
    // After Stripe payment intent succeeds on frontend, we show overlay
    // and wait for backend webhook confirmation via socket ORDER_PAID message
    window.paymentOverlayStartTime = Date.now(); // Track when overlay started showing
    setShowPaymentOverlay(true);
    setIsProcessing(true);
  };

  const handlePaymentError = (error) => {
    toast.error(`Payment failed: ${error}`);
  };

  const handlePaymentCancel = () => {
    setShowPaymentOverlay(false);
    navigate('/basket');
  };

  if (isCreatingOrder) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center z-50">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold mb-2">Creating Order...</h2>
          <p className="text-gray-300">Please wait while we prepare your order</p>
        </div>
      </div>
    );
  }

  if (showPaymentOverlay && orderId) {
    return (
      <SubmittingOrderOverlay
        deliveryType={deliveryType}
        pharmacy={pharmacy}
        orderId={orderId}
      />
    );
  }

  if (!orderId) {
    return null; // Wait for order to be created
  }

  return (
    <div className="min-h-screen">
      <StripePayment
        orderId={orderId}
        onSuccess={handlePaymentSuccess}
        onError={handlePaymentError}
        onCancel={handlePaymentCancel}
      />
    </div>
  );
};

export default PaymentPage;